import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Typeahead, Menu, MenuItem } from 'react-bootstrap-typeahead';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getAssetUrl } from '../../../utils/GetAssetUrl';
import { FormattedMessage } from 'react-intl';
import { makeSelectIsLoading } from '../../../store/render-controller/render-controller.selectors';
import {
  ITypeaheadItem,
  ITypeaheadButton,
  ITypeaheadList,
  ITypeaheadProps,
  ETypeaheadFormatter,
  ITypeaheadSelectedListProps,
  EUserType,
} from '../../../domain/typeahead.interfaces';
import './Typeahead.component.scss';
import _ from 'lodash';
import { EItemButtons } from '../../../domain/typeahead.interfaces';

function TypeaheadMenu(props: { results: ITypeaheadList[]; menuProps: any }) {
  const { results, menuProps } = props;

  return (
    <Menu
      className={
        !!results[0].data.length || (!!results[1] && !!results[1].data.length)
          ? 'typeahead-list'
          : ''
      }
      id='optionslist'
      {...menuProps}
    >
      {!!results.length &&
        results.map((list: ITypeaheadList, idx: number) => {
          return (
            <div key={idx}>
              {!!list.data.length && (
                <Menu.Header>
                  <FormattedMessage id={`common.${list.title}`} />
                </Menu.Header>
              )}
              {list.data.map((item: ITypeaheadItem, itemIdx: number) => (
                <TypeaheadMenuItem
                  result={item}
                  buttons={list.buttons}
                  onClickAction={list.onClickAction}
                  key={itemIdx}
                />
              ))}
            </div>
          );
        })}
    </Menu>
  );
}

function ItemContent(props: {
  result: ITypeaheadItem;
  buttons?: ITypeaheadButton[];
  onClickAction?: (...args: any[]) => void;
}) {
  const { result, buttons, onClickAction } = props;
  const filteredButtons =
    !!buttons &&
    buttons.filter((button: ITypeaheadButton) => {
      if (
        !!result.unallowedButtons &&
        !!(result.unallowedButtons as EItemButtons[]).length
      ) {
        return (result.unallowedButtons as EItemButtons[]).some(
          (unallowedButton: EItemButtons) => button.type !== unallowedButton,
        );
      } else {
        return button;
      }
    });

  return (
    <div
      className={`minicard user-type-${
        result.userType !== undefined ? result.userType : '4'
      }`}
      onClick={() =>
        !!onClickAction ? onClickAction(result.original) : undefined
      }
    >
      <div className='minicard-image'>
        {result.image ? (
          <div
            style={{
              backgroundImage: `url(${getAssetUrl.imagesStatic(result.image)})`,
            }}
          />
        ) : (
          <div className='icon-container d-flex justify-container-center align-items-center'>
            <FontAwesomeIcon icon='user' />
          </div>
        )}
      </div>
      <div
        className={`minicard-info ${
          !!result.title && _.includes(result.title, '@') ? '' : 'capitalize'
        }`}
      >
        <h6>{result.title}</h6>
        {result.userType === EUserType.ADMIN ||
        result.userType === EUserType.INACTIVE ||
        result.userType === EUserType.EXTERNAL ? (
          <p>
            ( <FormattedMessage id={`common.user-type-${result.userType}`} /> )
          </p>
        ) : !!result.subtitle ? (
          <p>{result.subtitle}</p>
        ) : null}
      </div>
      <div className='minicard-buttons'>
        {filteredButtons &&
          filteredButtons.map((button: ITypeaheadButton, index: number) => {
            return (
              <div
                key={index}
                className={button.className ? button.className : ''}
                onClick={(e: any) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!button.action) {
                    return;
                  }
                  button.action(result.original);
                }}
              >
                {button.label && <p>{button.label}</p>}
                {button.icon && <FontAwesomeIcon icon={button.icon} />}
                {button.image && <img src={button.image} alt='' />}
              </div>
            );
          })}
      </div>
    </div>
  );
}

function TypeaheadMenuItem(props: {
  result: ITypeaheadItem;
  buttons?: ITypeaheadButton[];
  onClickAction?: (...args: any[]) => void;
}) {
  const { result, buttons, onClickAction } = props;

  return (
    <MenuItem option={result} position={+result.id}>
      <ItemContent
        result={result}
        buttons={buttons}
        onClickAction={onClickAction}
      />
    </MenuItem>
  );
}

function TypeaheadSelectedList({
  oldSelected,
  newSelected,
  removeNewSelectedFromList,
  removeOldSelectedFromList,
}: ITypeaheadSelectedListProps) {
  function removeOldSelected(item: any) {
    removeOldSelectedFromList(item);
  }

  function removeNewSelected(item: ITypeaheadItem) {
    removeNewSelectedFromList(item);
  }

  const newSelectedButtons: ITypeaheadButton[] = [
    {
      icon: 'trash',
      action: removeNewSelected,
      className: 'minicard-delete',
    },
  ];

  const oldSelectedButtons: ITypeaheadButton[] = [
    {
      icon: 'trash',
      action: removeOldSelected,
      className: 'minicard-delete',
      type: EItemButtons.DELETE,
    },
  ];

  return (
    <div className='mb-3'>
      {!!oldSelected.length &&
        oldSelected.map((item: ITypeaheadItem, idx: number) => (
          <ItemContent
            key={`old-${idx}`}
            result={item}
            buttons={oldSelectedButtons}
          />
        ))}
      {!!newSelected.length &&
        newSelected.map((item: ITypeaheadItem, idx: number) => (
          <ItemContent
            key={`new-${idx}`}
            result={item}
            buttons={newSelectedButtons}
          />
        ))}
    </div>
  );
}

function filterByApi(option: any) {
  return option;
}

function filterByComponent(option: any, pattern: string, filterKeys: string[]) {
  const newOption = { ...option };
  newOption.data = option.data.filter((item: ITypeaheadItem) =>
    filterKeys.some(key =>
      _.includes(item[key as ETypeaheadFormatter], pattern),
    ),
  );
  return newOption;
}

export function TypeaheadComponent({
  label,
  addRef,
  handleInputChange,
  handleOnBlur,
  handleOnFocus,
  options,
  filterKeys,
  errorTrigger,
  meta,
  input,
  withList,
  maxListLength,
  oldSelected,
  handleRemoveOldSelected,
  handleAddNewSelected,
}: ITypeaheadProps) {
  const isLoading = useSelector(makeSelectIsLoading());
  const [newSelected, setNewSelected] = useState<ITypeaheadItem[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<ITypeaheadList[]>([]);

  function removeNewSelectedFromList(selected: ITypeaheadItem) {
    setNewSelected(
      newSelected.filter((item: ITypeaheadItem) => item.id !== selected.id),
    );
  }

  function removeOldSelectedFromList(selected: ITypeaheadItem) {
    return !!handleRemoveOldSelected
      ? handleRemoveOldSelected(selected)
      : undefined;
  }

  function addNewSelectedFromList() {
    return !!handleAddNewSelected
      ? handleAddNewSelected(newSelected)
      : undefined;
  }

  // eslint-disable-next-line
  useEffect(() => addNewSelectedFromList(), [newSelected]);

  useEffect(() => {
    if (!options) {
      return;
    }
    const newOpts = options.map((opt: ITypeaheadList) => {
      const newOpt = { ...opt };
      newOpt.data = opt.data.filter((item: ITypeaheadItem) => {
        if (!!oldSelected) {
          return (
            !newSelected.some(x => x.id === item.id) &&
            !oldSelected.some(x => x.id === item.id)
          );
        } else {
          return !newSelected.some(x => x.id === item.id);
        }
      });
      return newOpt;
    });
    setFilteredOptions(newOpts);
    // eslint-disable-next-line
  }, [options]);

  function checkListLength() {
    if (!maxListLength) {
      return true;
    }
    const newSelectedLength = !!newSelected.length ? newSelected.length : 0;
    const oldSelectedLength =
      !!oldSelected && !!oldSelected.length ? oldSelected.length : 0;
    if (!!maxListLength) {
      return newSelectedLength + oldSelectedLength < maxListLength
        ? true
        : false;
    }
  }

  return (
    <div className='field-container typeahead-container'>
      <div className={`${checkListLength() ? '' : 'hide'}`}>
        <Typeahead
          ref={(el: any) => (addRef ? addRef(el) : null)}
          isLoading={isLoading}
          options={
            !!withList
              ? filteredOptions
                ? filteredOptions
                : []
              : options
              ? options
              : []
          }
          inputProps={{
            className: `field`,
            spellCheck: false,
            ...input,
          }}
          placeholder={label}
          labelKey={(option: ITypeaheadList) => option.title as string}
          maxResults={20}
          onChange={(val: any) => {
            if (!!withList) {
              setNewSelected([...newSelected, ...val]);
              handleOnBlur();
            }
          }}
          onBlur={(event: any) => {
            const target = event.nativeEvent.relatedTarget;
            if (!!target && target.className === 'rbt clearfix open') {
              return;
            }
            return handleOnBlur ? handleOnBlur() : undefined;
          }}
          onFocus={() => (handleOnFocus ? handleOnFocus() : undefined)}
          onInputChange={handleInputChange}
          id={label + Math.random()}
          filterBy={(option, inputValue) => {
            return !!filterKeys && !!filterKeys.length
              ? filterByComponent(option, inputValue.text, filterKeys)
              : filterByApi(option);
          }}
          renderMenu={(results, menuProps) => (
            <TypeaheadMenu results={results} menuProps={menuProps} />
          )}
        />
        <div className='message-container'>
          {errorTrigger && meta && (
            <span className='field-error-message'>
              {meta && (
                <FormattedMessage
                  id={meta.error.id}
                  values={{ value: meta.error.value }}
                />
              )}
            </span>
          )}
        </div>
      </div>
      <div className='typeahead-selected-list-container'>
        {withList && (
          <TypeaheadSelectedList
            oldSelected={!!oldSelected ? oldSelected : []}
            newSelected={newSelected}
            removeNewSelectedFromList={removeNewSelectedFromList}
            removeOldSelectedFromList={removeOldSelectedFromList}
          />
        )}
      </div>
    </div>
  );
}
